import React from 'react';

export type ForcedAttr = 'data-hovered' | 'data-pressed' | 'data-focus-visible';

interface ForceInteractionStateProps {
  /** React Aria state attribute to force onto the target element. */
  attr: ForcedAttr;
  /**
   * CSS selector, relative to this wrapper, identifying the descendant that
   * should receive the forced attribute. Defaults to the wrapper's first
   * element child.
   */
  selector?: string;
  children: React.ReactNode;
}

/**
 * Forces one of React Aria Components' interaction-state data attributes
 * (`data-hovered`, `data-pressed`, `data-focus-visible`) onto a rendered
 * element, for gallery/screenshot purposes only.
 *
 * This is NOT a substitute for real interaction. React Aria's hover/press/
 * focus-visible hooks manage these attributes from real pointer/keyboard
 * events, not from props — there is no supported way to pass `isHovered`
 * etc. in. Setting the attribute imperatively via a ref, after mount, is the
 * only way to force the state without dispatching synthetic events. A
 * MutationObserver re-asserts it, because real interaction makes React reclaim
 * the attribute (see the comment on the effect).
 *
 * Critically, this only reproduces styling for CSS rules keyed off the RAC
 * attribute (e.g. `data-[hovered]`, `[data-focus-visible]`). Rules written
 * against the real `:hover` / `:focus-visible` pseudo-classes do not fire —
 * see the caption in `InteractionStateGrid` for how much of the cofactor
 * hover surface that actually excludes.
 */
export function ForceInteractionState({ attr, selector, children }: ForceInteractionStateProps) {
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  // Deps are static strings per call site, so this runs once per mount. It must
  // NOT run on every render: `UIDemoContent` holds all the demo's state, so any
  // keystroke elsewhere on the page re-renders every instance of this.
  React.useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const target = selector
      ? wrapper.querySelector<HTMLElement>(selector)
      : (wrapper.firstElementChild as HTMLElement | null);
    if (!target) {
      // A miss means a @chatsdet/ui component changed its DOM shape (e.g.
      // Select's trigger stopped being a <button>). The cell would then quietly
      // render as Rest — which reads as "this state has no styling", the exact
      // wrong conclusion — so make it discoverable instead of merely wrong.
      console.warn(`ForceInteractionState: no element matched "${selector ?? ':first-child'}" for ${attr}`);
      return;
    }
    target.setAttribute(attr, '');

    // React Aria owns this attribute, so it takes it back: really hovering a
    // forced cell makes RAC render `data-hovered="true"`, and the un-hover
    // renders `undefined`, which React removes — deleting the forced value with
    // it. Measured: hovering one cell dropped the page's forced-attribute count
    // from 5 to 4, and the cell silently reverted to looking like Rest. Re-assert
    // on removal so browsing the gallery can't blank a column.
    const observer = new MutationObserver(() => {
      if (!target.hasAttribute(attr)) target.setAttribute(attr, '');
    });
    observer.observe(target, { attributes: true, attributeFilter: [attr] });

    return () => {
      observer.disconnect();
      target.removeAttribute(attr);
    };
  }, [attr, selector]);

  return (
    <div ref={wrapperRef} className="contents">
      {children}
    </div>
  );
}
